/* eslint-disable multiline-ternary */
/* eslint-disable indent */
import {
  useEvent,
  useEventsOnViewLoad
} from '@emerald-works/react-event-bus-client'
import { Box, Divider, Grid, Typography } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import {
  AddToPlaylist,
  AuthorsCarousel,
  Container,
  GoogleAd,
  PageContainer,
  Pagination
} from '../../components'
import { useAddToBookmarks } from '../../components/add-to-bookmarks'
import { useAddToPlaylist } from '../../components/add-to-playlist-dialog'
import {
  Breadcrumbs,
  CategoryMetaTags,
  Header,
  Topics
} from '../../components/category-landing'
import { useFilterState } from '../../components/filter-bar/hooks'
import { GoogleAdContext } from '../../components/google-ad/google-ad-context'
import { FilterBar } from '../../components/search-results'
import { useSEO } from '../../components/seo-context'
import { UserGroupPolicy } from '../../components/user-group-policy'
import { ALLOW_PLAYLISTS_GROUP } from '../../components/user-group-policy/permissions'
import { categoryPath, categoryUrl, contentUrl } from '../../helpers/url'
import {
  useFormFactor,
  useGoogleAdsense,
  useGroup,
  usePage,
  useProducts,
  useRedirect
} from '../../hooks'
import useCampaignList from '../../hooks/useCampaignList'
import { categorySlice, searchResultsSlice } from '../../reducers'
import { ALL_NON_PAYING } from '../../services/cognito/userGroups'
import Error from '../error'
import NotFound from '../not-found'
import { useEventFilters, useSearchQuery } from '../search-results/hooks'
import useContentSearchedEvent from '../search-results/hooks/useContentSearchedEvent'
import { sorters } from '../search-results/params'
import { CategoryContentCard } from './components/category-content-card'
import { CategoryPromoteFeatureCard } from './components/category-promote-feature-card'
import { useStyles } from './style'

const CategoryLanding = () => {
  const { isInGroup } = useGroup()
  const isNonPayingUser = isInGroup(ALL_NON_PAYING)
  const { sendAdsRequest, adsRequestSent } = useContext(GoogleAdContext)

  const classes = useStyles()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isMobile, isDesktop } = useFormFactor()
  const { listColumns } = useCampaignList()
  const { locationId, '*': slug } = useParams()

  const category = useSelector(categorySlice.selectors.selectCategory)
  const children = useSelector(categorySlice.selectors.selectChildren)
  const path = useSelector(categorySlice.selectors.selectPath)

  const [error, setError] = useState()

  const [getCategory, getSearchResults, getParentSearchCounts] = useEvent([
    {
      eventName: 'categoryGetInfo',
      onStart: () => setError(),
      onSuccess: payload =>
        dispatch(categorySlice.actions.setCategory(payload)),
      onError: error => setError(error)
    },
    searchResultsSlice.eventBus.getSearchResults,
    searchResultsSlice.eventBus.getParentSearchCounts
  ])

  const { redirect } = useSEO(
    getCategory.hasBeenTriggered &&
      !getCategory.isWorking &&
      getSearchResults.hasBeenTriggered &&
      !getSearchResults.isWorking &&
      getParentSearchCounts.hasBeenTriggered &&
      !getParentSearchCounts.isWorking
  )

  useEventsOnViewLoad(() => {
    if (locationId) getCategory.trigger({ sk: locationId })
  }, [getCategory, locationId])

  useEffect(() => {
    if (locationId && category && !slug) {
      navigate(categoryUrl(category, path), { replace: true })
      redirect(`Location: ${origin}${categoryUrl(category, path)}`)
    }
  }, [locationId, slug, redirect, navigate, category, path])

  const { getEventFilters } = useEventFilters()

  const { params, filters, clearFilters, addFilter } = useSearchQuery()

  const filterProps = ['type']
  useFilterState(filters, filterProps)

  const results = useSelector(searchResultsSlice.selectors.selectResults)
  const total = useSelector(searchResultsSlice.selectors.selectTotal)
  const counts = useSelector(searchResultsSlice.selectors.selectCounts)
  const parentCounts = useSelector(
    searchResultsSlice.selectors.selectParentCounts
  )

  const hasQuiz = counts?.types?.find(type => type.key === 'quiz') !== undefined

  const parentHasQuiz =
    parentCounts?.types?.find(type => type.key === 'quiz') !== undefined

  const parentCategory = path.find(item => item?.sk === category?.parent?.sk)

  const { isBookmarked, handleBookmark, bookmarking } = useAddToBookmarks({
    from: 'category-landing'
  })

  const { pageStart, pageSize, pageCurrent, pageCount, calcPageStart } =
    usePage(params, total)

  useEffect(() => {
    if (pageStart < 1 || !pageSize) return

    const args = {
      filters: {
        categories: [locationId],
        ...getEventFilters(filters),
        sort: 'createdAt:desc'
      },
      size: pageSize,
      from: pageStart - 1
    }
    getSearchResults.trigger(args)
    if (category?.parent?.sk) {
      getParentSearchCounts.trigger({
        filters: {
          categories: [category.parent.sk],
          sort: 'createdAt:desc'
        },
        size: pageSize,
        from: pageStart - 1
      })
    }
    return () => dispatch(searchResultsSlice.actions.reset())
  }, [
    getSearchResults,
    filters,
    pageStart,
    pageSize,
    getEventFilters,
    locationId,
    dispatch,
    category,
    getParentSearchCounts
  ])

  useContentSearchedEvent({
    event: getSearchResults,
    filters,
    from: 'category-landing',
    category
  })

  const { showAddToPlaylist, getPlaylistArticle } = useAddToPlaylist()

  const getListItems = () => {
    const items = getSearchResults.isWorking
      ? Array(pageSize).fill({})
      : results

    const firstRowCapacity = Math.floor(12 / listColumns.lg)

    // Only inject promo card if we have enough items to fill at least one row AND there are quizzes available
    if (items.length >= firstRowCapacity && (hasQuiz || parentHasQuiz)) {
      const modifiedItems = [...items]
      // Insert promo card as the first item in the first row
      const promoPosition = 0
      modifiedItems.splice(promoPosition, 0, { isPromoCard: true })
      return modifiedItems
    }

    return items
  }

  const getItemsWithBanners = () => {
    const items = getListItems()
    const itemsWithBanners = []

    // Determine number of columns based on form factor and campaign configuration
    let columns
    if (isMobile) {
      columns = 1
    } else if (isDesktop) {
      // For desktop, determine columns based on campaign configuration
      if (listColumns.lg === 4) {
        columns = 3 // With left banner: 3 columns
      } else if (listColumns.lg === 3) {
        columns = 4 // Without left banner: 4 columns
      } else {
        columns = 12 / listColumns.lg
      }
    } else {
      // For tablets, use md breakpoint
      columns = 12 / listColumns.md
    }

    // Define banner rules based on number of columns
    const bannerRules = {
      4: { itemsBeforeBanner: 8, rows: 2 },
      3: { itemsBeforeBanner: 6, rows: 2 },
      2: { itemsBeforeBanner: 4, rows: 2 },
      1: { itemsBeforeBanner: 3, rows: 3 }
    }

    const rule = bannerRules[columns] || bannerRules[1]
    const itemsBeforeBanner = rule.itemsBeforeBanner

    let bannerCount = 0

    // Process items and insert banners according to rules
    for (let i = 0; i < items.length; i++) {
      // Add the current item
      itemsWithBanners.push(items[i])

      // Check if we should insert a banner after this item
      const itemIndex = i + 1 // 1-based index
      const shouldInsertBanner =
        itemIndex % itemsBeforeBanner === 0 && // Every itemsBeforeBanner items
        itemIndex < items.length // Not after the last item

      if (shouldInsertBanner) {
        itemsWithBanners.push({
          isHorizontalBanner: true,
          bannerId: `banner-${bannerCount}`
        })
        bannerCount++
      }
    }

    // Always add a banner after the last item if there isn't one already
    if (
      itemsWithBanners.length > 0 &&
      !itemsWithBanners[itemsWithBanners.length - 1]?.isHorizontalBanner
    ) {
      itemsWithBanners.push({
        isHorizontalBanner: true,
        bannerId: `banner-${bannerCount}`
      })
    }

    return itemsWithBanners
  }

  const handlePageChange = (evt, newPage) =>
    navigate(addFilter({ start: calcPageStart(newPage) }))

  const handleCardSelect = item => navigate(contentUrl(item, 'category'))

  const handleFilterApply = ({ name, value }) =>
    navigate(addFilter({ [name]: value }))

  const handleQuizFilterApply = () => {
    if (hasQuiz) {
      navigate(addFilter({ type: 'quiz' }))
    } else if (parentHasQuiz) {
      navigate(
        contentUrl(
          { locationId: parentCategory?.sk, ...parentCategory },
          'category',
          null,
          { type: 'quiz' }
        )
      )
    }
  }

  const handleFilterClearAll = () => navigate(clearFilters())

  const handleCategoryChange = category => {
    if (category) {
      dispatch(
        categorySlice.actions.setCategory({
          category,
          childrenCategories: [],
          pathToRoot: categoryPath(category, path)
        })
      )
    }
  }

  const { useAdsense } = useGoogleAdsense()

  useEffect(() => {
    if (results.length > 0 && isNonPayingUser && !adsRequestSent) {
      sendAdsRequest()
    }
  }, [results, isNonPayingUser, adsRequestSent, sendAdsRequest])

  if (error) {
    return error.statusCode === 404 ? <NotFound /> : <Error />
  }

  return (
    <PageContainer>
      {category && <CategoryMetaTags category={category} />}
      <UserGroupPolicy
        accessGroup={ALLOW_PLAYLISTS_GROUP}
        componentAllow={
          <AddToPlaylist
            article={getPlaylistArticle()}
            backText='Back to Category'
          />
        }
      />
      <Container maxContentWidth={1200} inset={3}>
        <Breadcrumbs
          path={path}
          onClick={handleCategoryChange}
          className={classes.breadcrumbs}
          loading={getCategory.isWorking}
        />
        <Header category={category} loading={getCategory.isWorking} />
        {(getCategory.isWorking || children?.length > 0) && (
          <Topics
            category={category}
            topics={children}
            path={path}
            onClick={handleCategoryChange}
            loading={getCategory.isWorking}
            className={classes.topics}
            useBrandColors
          />
        )}
        <Grid container>
          <Grid item xs={12}>
            <FilterBar
              counts={counts}
              sorters={sorters}
              values={filters}
              onApply={handleFilterApply}
              onClearAll={handleFilterClearAll}
              category={category}
              onQuizFilterClick={handleQuizFilterApply}
              classes={{ root: classes.filterBar }}
              parentCounts={parentCounts}
              parentCategory={parentCategory}
            />
            <Grid container spacing={3} data-test='list-container'>
              {getItemsWithBanners().map((item, index) => {
                if (item?.isHorizontalBanner) {
                  return (
                    useAdsense && (
                      <Grid
                        item
                        xs={12}
                        key={item.bannerId}
                        data-test={`horizontal-banner-${index}`}
                      >
                        <GoogleAd
                          adKey={`category-landing-${item.bannerId}`}
                          adPosition='category-landing-horizontal-banner'
                          showCta={false}
                        />
                      </Grid>
                    )
                  )
                }

                if (item?.isPromoCard) {
                  return (
                    <Grid
                      item
                      key={item?.friendlyUrl ?? index}
                      xs={listColumns.xs}
                      sm={listColumns.sm}
                      md={listColumns.md}
                      lg={listColumns.lg}
                      xl={listColumns.xl}
                      data-test={`list-item-${index}`}
                    >
                      <CategoryPromoteFeatureCard
                        dataTest={`category-promote-feature-card-${index}`}
                        category={
                          hasQuiz
                            ? category
                            : parentHasQuiz
                            ? parentCategory
                            : null
                        }
                        loading={getSearchResults.isWorking}
                        onQuizFilter={handleQuizFilterApply}
                      />
                    </Grid>
                  )
                }

                // Render regular content card
                return (
                  <Grid
                    item
                    key={item?.friendlyUrl ?? index}
                    xs={listColumns.xs}
                    sm={listColumns.sm}
                    md={listColumns.md}
                    lg={listColumns.lg}
                    xl={listColumns.xl}
                    data-test={`list-item-${index}`}
                  >
                    <CategoryContentCard
                      content={!getSearchResults.isWorking && item}
                      onSelect={() => handleCardSelect(item)}
                      onBookmark={() => handleBookmark(item)}
                      bookmarked={isBookmarked(item)}
                      actionLoading={bookmarking(item)}
                      onAddToPlaylist={showAddToPlaylist}
                      dataTest={`category-content-${index}`}
                    />
                  </Grid>
                )
              })}
            </Grid>
          </Grid>
        </Grid>
        <Pagination
          page={pageCurrent}
          count={pageCount}
          size={pageSize}
          onChange={handlePageChange}
          className={classes.pagination}
        />

        <Divider className={classes.divider} />
        <Box className={classes.authorSection}>
          <Typography variant='h5' className={classes.authorSectionTitle}>
            Explore Authors
          </Typography>
          <AuthorsCarousel loading={getCategory.isWorking} />
        </Box>
      </Container>
    </PageContainer>
  )
}

const Page = () => {
  const { hasContentHubAccess } = useProducts()
  const { standaloneRedirect } = useRedirect()

  useEffect(() => {
    if (!hasContentHubAccess) standaloneRedirect()
  }, [hasContentHubAccess, standaloneRedirect])

  if (!hasContentHubAccess) return null

  return <CategoryLanding />
}

export default React.memo(Page)
